import { createSelector } from 'reselect';
import { parseISO } from 'date-fns';

import {
  reportsVenuesBookings as reportsVenuesBookingsEntity,
  reportsVenuesRevenue as reportsVenuesRevenueEntity,
} from 'store/saga/entities/bound';
import { selectRouteParams, selectUserVenues, selectUserVenuesAsMap } from 'selectors/general';

import { VENUES_REPORTS_TYPES } from '../Stats.constants';

export const selectReportsVenuesControls = state => state.reportsVenues;

const selectSelectedVenueIds = createSelector(
  selectReportsVenuesControls,
  venuesReport => venuesReport.selectedVenueIds
);

const selectSelectedVenueIdsSet = createSelector(selectSelectedVenueIds, selectedVenueIds => new Set(selectedVenueIds));

const selectSelectedVenues = createSelector(
  selectSelectedVenueIdsSet,
  selectUserVenues,
  (selectedVenueIdsSet, venues) => venues.filter(({ venueId }) => selectedVenueIdsSet.has(venueId))
);

const selectSelectedVenueCurrencyCodesSet = createSelector(
  selectSelectedVenues,
  selectedVenues => new Set(selectedVenues.map(({ currencyCode }) => currencyCode))
);

export const selectActiveGraphIndex = createSelector(
  selectReportsVenuesControls,
  venuesReport => venuesReport.activeGraphIndex
);

export const selectViewByUnit = createSelector(selectReportsVenuesControls, venuesReport => venuesReport.viewByUnit);

export const selectViewByRange = createSelector(
  selectReportsVenuesControls,
  selectViewByUnit,
  (venuesReport, viewByUnit) => {
    switch (viewByUnit) {
      case 'day':
        return venuesReport.viewByRangeDay;
      case 'week':
        return venuesReport.viewByRangeWeek;
      case 'month':
        return venuesReport.viewByRangeMonth;
      case 'year':
        return venuesReport.viewByRangeYear;
      default:
        return venuesReport.viewByRangeMonth;
    }
  }
);

const selectReportsVenuesBookings = createSelector(
  state => state,
  selectViewByUnit,
  selectViewByRange,
  (state, viewByUnit, viewByRange) => reportsVenuesBookingsEntity.select(state, `${viewByUnit}_${viewByRange}`)
);

const selectReportsVenuesRevenue = createSelector(
  state => state,
  selectViewByUnit,
  selectViewByRange,
  (state, viewByUnit, viewByRange) => reportsVenuesRevenueEntity.select(state, `${viewByUnit}_${viewByRange}`)
);

// each entry in summaryData represents a summaryCard (revenue in different currencyCodes/bookings)
export const selectReportsVenues = createSelector(
  selectUserVenuesAsMap,
  selectRouteParams,
  selectSelectedVenueIdsSet,
  selectSelectedVenueCurrencyCodesSet,
  selectReportsVenuesBookings,
  selectReportsVenuesRevenue,
  (
    venuesMap,
    { venuesReportType },
    selectedVenueIdsSet,
    selectedVenueCurrencyCodesSet,
    reportsVenuesBookings,
    reportsVenuesRevenue
  ) => {
    const summaryCardsData = [];

    const nonRevenueCleaningFn = nonRevenueForUnitOfTime =>
      Object.keys(nonRevenueForUnitOfTime).reduce(
        (memo, key) => {
          if (key === 'startDate' || key === 'endDate') {
            memo[key] = parseISO(nonRevenueForUnitOfTime[key]);
          } else if (selectedVenueIdsSet.has(Number(key))) {
            memo[key] = nonRevenueForUnitOfTime[key];
            memo.total += nonRevenueForUnitOfTime[key];
          }
          return memo;
        },
        { total: 0 }
      );

    // eslint-disable-next-line default-case
    switch (venuesReportType) {
      case VENUES_REPORTS_TYPES.BOOKINGS:
        // populate card with the appropriately transformed data if it's loaded, or null if it's not
        summaryCardsData.push({
          type: 'bookings',
          isRevenueCard: false,
          isLoading: !reportsVenuesBookings.loaded,
          cleanData: reportsVenuesBookings.loaded
            ? reportsVenuesBookings.data.bookings.map(nonRevenueCleaningFn)
            : null,
        });
        break;

      case VENUES_REPORTS_TYPES.REVENUE:
        // generate a summaryCard for each currency in revenue
        summaryCardsData.push(
          // populate cards with the appropriately transformed data if it's loaded, or create the right number of dummy loading cards if it's not
          ...(reportsVenuesRevenue.loaded
            ? Object.keys(reportsVenuesRevenue.data.revenue)
                // remove summaryCharts representing revenue in currencies that no selected venue uses
                .filter(currencyCode => selectedVenueCurrencyCodesSet.has(currencyCode.toUpperCase()))
                .map(currencyCode => ({
                  type: currencyCode,
                  isRevenueCard: true,
                  isLoading: false,
                  cleanData: reportsVenuesRevenue.data.revenue[currencyCode].map(revenueForUnitOfTime =>
                    Object.keys(revenueForUnitOfTime).reduce(
                      (memo, key) => {
                        if (key === 'startDate' || key === 'endDate') {
                          memo[key] = parseISO(revenueForUnitOfTime[key]);
                        } else if (selectedVenueIdsSet.has(Number(key))) {
                          memo[key] = revenueForUnitOfTime[key].amount; // { revenue, currencyCode } obj to unitless quantity (same as bookings/spots)
                          memo.total += revenueForUnitOfTime[key].amount;
                        }
                        return memo;
                      },
                      { total: 0 }
                    )
                  ),
                }))
            : [...selectedVenueCurrencyCodesSet].map(currencyCode => ({
                type: currencyCode,
                isRevenueCard: true,
                isLoading: true,
                cleanData: null,
              })))
        );

        // generate a dummy card with an empty chart if all venues are unselected so the page behaves the same as the other venue level pages and doesn't go totally blank
        if (summaryCardsData.length === 0) {
          summaryCardsData.push({
            type: 'emptyRevenue',
            isRevenueCard: false, // Well, it is a revenue card, but we don't want to show any currency symbol stuff since it's an empty state
            isLoading: !reportsVenuesRevenue.loaded,
            cleanData: reportsVenuesRevenue.loaded
              ? // doesn't matter which currency you use because we only need dates and are excluding all venues
                reportsVenuesRevenue.data.revenue[Object.keys(reportsVenuesRevenue.data.revenue)[0]].map(
                  revenueForUnitOfTime => {
                    return Object.keys(revenueForUnitOfTime).reduce(
                      (memo, key) => {
                        if (key === 'startDate' || key === 'endDate') {
                          memo[key] = parseISO(revenueForUnitOfTime[key]);
                        }
                        return memo;
                      },
                      { total: 0 }
                    );
                  }
                )
              : null,
          });
        }
        break;
    }

    return summaryCardsData.map(summaryCardData => {
      const { type, isRevenueCard, isLoading, cleanData } = summaryCardData;
      // if it's a loading card, populate the rest of the fields with null and return early
      if (isLoading) {
        return { type, isRevenueCard, isLoading, venueIds: null, legend: null, data: null };
      }

      // or actually do all the real calculations on the data
      // \/ getting info to generate legend, process data \/
      const venueTotalMap = cleanData.reduce((memo, dataForUnitOfTime) => {
        Object.keys(dataForUnitOfTime)
          .filter(key => key !== 'startDate' && key !== 'endDate' && key !== 'total')
          .forEach(venueId => {
            if (memo[venueId]) {
              memo[venueId] += dataForUnitOfTime[venueId];
            } else {
              memo[venueId] = dataForUnitOfTime[venueId];
            }
          });
        return memo;
      }, {});

      const topVenuesInfo = Object.entries(venueTotalMap)
        // sort venues in descending order of revenue/bookings/spots (id as a tiebreaker)
        .sort(([aId, aVal], [bId, bVal]) => {
          if (aVal > bVal) return -1;
          if (aVal < bVal) return 1;
          return aId > bId ? -1 : 1;
        })
        // compress anything past 6th place into unlabeled field representing "all remaining venues" and
        // keep track of its total and the count of excluded venues
        .reduce((topVenuesInfoArr, [venueId, total], i) => {
          if (i < 6) {
            topVenuesInfoArr.push({
              venueId: Number(venueId),
              total,
              count: 1,
            });
          } else if (i === 6) {
            topVenuesInfoArr[topVenuesInfoArr.length - 1].venueId = null;
            topVenuesInfoArr[topVenuesInfoArr.length - 1].total += total;
            topVenuesInfoArr[topVenuesInfoArr.length - 1].count += 1;
          } else {
            topVenuesInfoArr[topVenuesInfoArr.length - 1].total += total;
            topVenuesInfoArr[topVenuesInfoArr.length - 1].count += 1;
          }
          return topVenuesInfoArr;
        }, []);

      // IDs of top venues for quick lookup later
      const topVenueIdsSet = topVenuesInfo.reduce((venueIds, venue) => {
        venueIds.add(venue.venueId);
        return venueIds;
      }, new Set());

      // /\ getting info to generate legend, process data /\
      // \/ actually generating legend and processing data \/

      // arr of venueIds for rendering graph; allRemaining is a convenient key to use because
      // of victory's API for mapping through arrays/objects when rendering bar chart bars
      const venueIds = topVenuesInfo.map(({ venueId }) => (venueId ? String(venueId) : 'allRemaining'));

      // get necessary info for each legendItem, accounting for allRemaining field
      const legend = topVenuesInfo.map(({ venueId, total, count }) => ({
        venueId: venueId || null,
        venueName: venueId ? venuesMap[venueId].venueName : null,
        venueSubtitle: venueId ? venuesMap[venueId].venueSubtitle : null,
        total,
        count,
      }));

      // compress any venues past sixth place into allRemaining pseudo-venueId
      const data = cleanData.map(dataForUnitOfTime =>
        Object.keys(dataForUnitOfTime)
          .filter(key => key !== 'startDate' && key !== 'endDate' && key !== 'total')
          .reduce(
            (memo, venueId) => {
              if (topVenueIdsSet.has(Number(venueId))) {
                memo[venueId] = dataForUnitOfTime[venueId];
              } else {
                memo.allRemaining += dataForUnitOfTime[venueId];
              }
              return memo;
            },
            {
              allRemaining: 0,
              startDate: dataForUnitOfTime.startDate,
              endDate: dataForUnitOfTime.endDate,
              total: dataForUnitOfTime.total,
            }
          )
      );

      // actual individual SummaryCardData obj
      return {
        type,
        isRevenueCard,
        isLoading,
        venueIds,
        legend,
        data,
      };
    });
  }
);
